// Platform administrators console — visible only to founders.
// Grants and revokes run through callables so the `admin` custom claim and
// users/{uid}.role always change together, with an audit trail behind them.

import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  Crown,
  UserPlus,
  Loader2,
  Trash2,
  AlertTriangle,
  Mail,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';
import { cn } from '../../lib/utils';

interface PlatformAdmin {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string | null;
  isSuperAdmin: boolean;
  grantedBy: string | null;
  grantedAt: any;
}

function formatGranted(value: any): string {
  const date = value?.toDate?.() || (value?._seconds ? new Date(value._seconds * 1000) : null);
  return date ? date.toLocaleDateString() : '';
}

export function AdminTeam() {
  const { isSuperAdmin, loading: checkingAccess } = useSuperAdmin();

  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [granting, setGranting] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const notify = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadAdmins = useCallback(async () => {
    try {
      const listAdmins = httpsCallable(functions, 'listPlatformAdmins');
      const result = await listAdmins({});
      setAdmins(((result.data as any)?.admins || []) as PlatformAdmin[]);
    } catch (err: any) {
      notify('error', err?.message || 'Could not load the admin roster');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (checkingAccess) return;
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    loadAdmins();
  }, [isSuperAdmin, checkingAccess, loadAdmins]);

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setGranting(true);
    try {
      const grant = httpsCallable(functions, 'grantPlatformAdmin');
      await grant({ email: email.trim() });
      notify('success', `${email.trim()} is now a platform admin`);
      setEmail('');
      await loadAdmins();
    } catch (err: any) {
      notify('error', err?.message || 'Could not grant admin access');
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (target: PlatformAdmin) => {
    setBusyUid(target.uid);
    try {
      const revoke = httpsCallable(functions, 'revokePlatformAdmin');
      await revoke({ uid: target.uid });
      notify('success', `Admin access removed from ${target.email}`);
      await loadAdmins();
    } catch (err: any) {
      notify('error', err?.message || 'Could not revoke admin access');
    } finally {
      setBusyUid(null);
    }
  };

  if (checkingAccess) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="h-6 w-6 text-primary animate-spin mx-auto" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="max-w-md mx-auto py-16 text-center">
        <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
        <h1 className="text-lg font-serif text-heading mb-1">Founders only</h1>
        <p className="text-sm text-body">
          Managing platform administrators is limited to FASH founders.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-serif text-heading">Platform Team</h1>
        <p className="text-sm text-body mt-1">
          Administrators can manage every store, order, and driver on FASH. Grant this carefully.
        </p>
      </div>

      {message && (
        <div
          className={cn(
            'rounded-xl px-4 py-3 text-sm',
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          )}
        >
          {message.text}
        </div>
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <h2 className="text-sm font-semibold text-heading flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-primary" />
            Add an administrator
          </h2>
          <p className="text-xs text-body">
            The person needs an existing FASH account. Their access takes effect the next time
            their session refreshes.
          </p>
          <form onSubmit={handleGrant} className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              required
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" disabled={granting}>
              {granting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Granting
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-1.5" />
                  Grant admin
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h2 className="text-sm font-semibold text-heading mb-3">
            Administrators ({admins.length})
          </h2>

          {loading ? (
            <div className="py-10 text-center">
              <Loader2 className="h-6 w-6 text-primary animate-spin mx-auto" />
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {admins.map((entry) => (
                <div key={entry.uid} className="flex items-center gap-3 py-3">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
                      entry.isSuperAdmin ? 'bg-amber-50 text-amber-600' : 'bg-primary/10 text-primary'
                    )}
                  >
                    {entry.isSuperAdmin ? (
                      <Crown className="h-4 w-4" />
                    ) : (
                      <ShieldCheck className="h-4 w-4" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-heading truncate">
                        {entry.displayName || entry.email}
                      </p>
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded-full text-[11px] font-semibold',
                          entry.isSuperAdmin
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-gray-100 text-gray-600'
                        )}
                      >
                        {entry.isSuperAdmin ? 'Founder' : 'Admin'}
                      </span>
                    </div>
                    <p className="text-xs text-body truncate">
                      {entry.email}
                      {entry.grantedBy ? ` · Added by ${entry.grantedBy}` : ''}
                      {formatGranted(entry.grantedAt) ? ` · ${formatGranted(entry.grantedAt)}` : ''}
                    </p>
                  </div>

                  {entry.isSuperAdmin ? (
                    <span className="text-xs text-body flex-shrink-0">Permanent</span>
                  ) : busyUid === entry.uid ? (
                    <Loader2 className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRevoke(entry)}
                      className="text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" />
                      Revoke
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminTeam;
